import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent } from './ui/dialog';
import { FileText, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { toast } from 'sonner';
import { getFileUrl } from '../utils/fileUtils';

const ProductCard = ({ product }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);
  
  // Combine main image with additional images for carousel
  const allImages = [product.image, ...(product.images || [])].map(getFileUrl);
  const hasMultipleImages = allImages.length > 1;

  const handleViewTech = () => {
    if (product.techSheet) {
      const techSheetUrl = getFileUrl(product.techSheet);
      window.open(techSheetUrl, '_blank');
    } else {
      toast.info('Ficha técnica no disponible para este producto');
    }
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  const openLightbox = () => {
    setLightboxImageIndex(currentImageIndex);
    setIsLightboxOpen(true);
  };

  const nextLightboxImage = (e) => {
    e.stopPropagation();
    setLightboxImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevLightboxImage = (e) => {
    e.stopPropagation();
    setLightboxImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  return (
    <>
      <Card className="group overflow-hidden border-stone-200 hover:border-clay-primary transition-all duration-300 hover:shadow-xl bg-white">
        <div 
          className="relative overflow-hidden h-64 bg-[#F5F1E8] flex items-center justify-center p-2 cursor-pointer"
          onClick={openLightbox}
        >
          <img
            src={allImages[currentImageIndex]}
            alt={product.name}
            className="w-full h-full object-contain transform group-hover:scale-105 transition-transform duration-500"
          />
          
          {/* Carousel controls */}
          {hasMultipleImages && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-stone-800 rounded-full p-2 shadow-lg transition-all opacity-0 group-hover:opacity-100"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-stone-800 rounded-full p-2 shadow-lg transition-all opacity-0 group-hover:opacity-100"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              
              {/* Indicators */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1">
                {allImages.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentImageIndex
                        ? 'bg-clay-primary w-4'
                        : 'bg-white/60'
                    }`}
                  />
                ))}
              </div>
            </>
          )}

          {/* Click to zoom indicator */}
          <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
            Click para ampliar
          </div>
        </div>
        
        <CardHeader className="pb-3">
          <CardTitle className="text-xl font-bold text-stone-800 group-hover:text-clay-primary transition-colors duration-200">
            {product.name}
          </CardTitle>
          <CardDescription className="text-stone-600">
            {product.description}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="pb-3">
          {product.specs && (
            <ul className="space-y-1">
              {product.specs.split('\n').filter(spec => spec.trim()).map((spec, index) => (
                <li key={index} className="text-sm text-stone-600 flex items-start">
                  <span className="text-clay-primary mr-2 mt-1">•</span>
                  <span className="flex-1">{spec.trim()}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
        
        <CardFooter>
          <Button
            onClick={handleViewTech}
            variant="outline"
            className="w-full border-clay-primary text-clay-primary hover:bg-clay-primary hover:text-white transition-colors duration-200 font-semibold"
          >
            <FileText className="mr-2 h-4 w-4" />
            Ver ficha técnica
          </Button>
        </CardFooter>
      </Card>

      {/* Lightbox Modal */}
      <Dialog open={isLightboxOpen} onOpenChange={setIsLightboxOpen}>
        <DialogContent className="max-w-5xl w-[90vw] h-[85vh] p-0 bg-stone-50/95 border-stone-200">
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Close button */}
            <button
              onClick={() => setIsLightboxOpen(false)}
              className="absolute top-4 right-4 z-50 bg-stone-800/80 hover:bg-stone-800 text-white rounded-full p-2 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Main image */}
            <div className="relative w-full h-full flex items-center justify-center p-8">
              <div className="flex items-center justify-center">
                <img
                  src={allImages[lightboxImageIndex]}
                  alt={`${product.name} - Imagen ${lightboxImageIndex + 1}`}
                  className="max-w-[85vw] max-h-[75vh] w-auto h-auto object-contain shadow-2xl"
                  style={{ margin: 'auto' }}
                />
              </div>

              {/* Navigation arrows for multiple images */}
              {hasMultipleImages && (
                <>
                  <button
                    onClick={prevLightboxImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-stone-800/80 hover:bg-stone-800 text-white rounded-full p-3 transition-all shadow-lg"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextLightboxImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-stone-800/80 hover:bg-stone-800 text-white rounded-full p-3 transition-all shadow-lg"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>

                  {/* Image counter */}
                  <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-stone-800/80 text-white px-4 py-2 rounded-full text-sm shadow-lg">
                    {lightboxImageIndex + 1} / {allImages.length}
                  </div>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProductCard;
